import { useEffect, useState } from "react";
import { CloudRainWind, X } from "lucide-react";

/**
 * Sliding announcement bar shown site-wide.
 *
 * Bumping `NOTICE_KEY` (below) forces the banner to reappear for users who
 * dismissed the previous notice. Keep the key format `announce:<yyyy-mm-dd>`
 * so it self-documents.
 */
const NOTICE_KEY = "announce:2026-07-02-pune-rains";

const MESSAGE = "All orders will be delayed by 2–3 days due to heavy rains and flood-type situation in Pune.";

// Duplicated once so the marquee loop is seamless (transform: -50%).
const LOOP = new Array(6).fill(MESSAGE);

export default function AnnouncementBar() {
  const [dismissed, setDismissed] = useState(true); // hidden until we know

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(NOTICE_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed) return null;

  const close = () => {
    try { localStorage.setItem(NOTICE_KEY, "1"); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="site-announcement-bar"
      className="relative bg-terra text-cream text-[11px] md:text-xs font-medium tracking-wide overflow-hidden border-b border-terra-hover/40"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center h-9 md:h-10 pr-9 md:pr-11">
        <div className="hidden sm:flex items-center gap-2 pl-4 pr-3 shrink-0">
          <CloudRainWind className="w-4 h-4" strokeWidth={1.75} />
          <span className="uppercase tracking-[0.22em] text-[10px] md:text-[11px] font-semibold text-cream/90">
            Weather notice
          </span>
        </div>

        {/* Marquee track — duplicated content so translate -50% loops seamlessly. */}
        <div className="flex-1 overflow-hidden" aria-hidden={false}>
          <div className="flex whitespace-nowrap animate-marquee will-change-transform">
            {[...LOOP, ...LOOP].map((msg, i) => (
              <span key={i} className="mx-8 md:mx-14 flex items-center gap-3">
                <CloudRainWind className="sm:hidden w-3.5 h-3.5" strokeWidth={1.75} />
                {msg}
                <span aria-hidden className="text-cream/50">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={close}
        aria-label="Dismiss weather notice"
        data-testid="site-announcement-bar-dismiss"
        className="absolute top-1/2 right-2 -translate-y-1/2 p-1.5 rounded-full text-cream/80 hover:text-cream hover:bg-terra-hover/60 transition-colors"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
